import clsx from 'clsx';
import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { type ButtonSize, BUTTON_SIZES } from '@builder4/shared/styles/constants';

interface SizeFieldProps {
  text: string;
  size?: ButtonSize;
  onClick?: (size: ButtonSize) => void;
  className?: string;
}

const sizes = BUTTON_SIZES;

export const SizeField: React.FC<SizeFieldProps> = ({
  size = 'small',
  onClick,
  text = 'Size',
  className,
}) => {
  const [selectedSize, setSelectedSize] = useState(size);
  const [highlightStyle, setHighlightStyle] = useState({ width: 0, left: 0 });
  const [isAnimated, setIsAnimated] = useState(false); // 👉 For animation

  const optionRefs = useRef<Record<string, HTMLButtonElement | null>>({
    small: null,
    medium: null,
    large: null,
  });

  useEffect(() => {
    if (size !== selectedSize) {
      setSelectedSize(size);
    }
  }, [size]);

  useLayoutEffect(() => {
    const activeEl = optionRefs.current[selectedSize];
    if (activeEl) {
      setHighlightStyle({
        width: activeEl.offsetWidth,
        left: activeEl.offsetLeft,
      });
    }
  }, [selectedSize]);

  const handleClick = (newSize: ButtonSize) => {
    setIsAnimated(true); // 👉 Run animation
    setSelectedSize(newSize);
    if (onClick) onClick(newSize);
  };

  return (
    <div className={clsx('flex w-full items-center justify-between gap-4', className)}>
      {text && <p className="text-button-sm font-medium text-neutral-500">{text}</p>}

      <div className="relative flex h-8 items-center rounded-md bg-neutral-50 shadow-[inset_0px_0px_2px_0px_rgba(0,0,0,0.12)]">
        <div
          className={`absolute top-0 z-20 flex h-full items-center justify-center rounded-md bg-[#ffffff] text-indigo-500 outline outline-offset-[-1px] outline-[#e9ecf0] ${isAnimated ? 'transition-all duration-300' : ''}`}
          style={{
            width: `${highlightStyle.width}px`,
            transform: `translateX(${highlightStyle.left}px)`,
          }}
        >
          <p className="text-button-sm !font-semibold capitalize">{selectedSize}</p>
        </div>

        {sizes.map((sz) => (
          <button
            key={sz}
            ref={(el) => {
              optionRefs.current[sz] = el;
            }}
            onClick={() => handleClick(sz)}
            className="text-button-sm relative z-10 min-w-10 cursor-pointer px-2 py-1.5 text-neutral-400 capitalize"
          >
            {sz}
          </button>
        ))}
      </div>
    </div>
  );
};
